import { useEffect, useState } from "react";
import { getScaffoldingFlags } from "@/lib/api";

// Z6 — read-only teacher panel: Level-5 scaffolding is never invisible, even when the final essay succeeds.
// Instructional signals, not grades. Technical ids stay behind a "details" disclosure.
const Row = ({ label, children, testid }) => (
  <div className="grid grid-cols-[130px_1fr] gap-3 text-[13px] leading-relaxed" data-testid={testid}>
    <div className="font-mono uppercase tracking-wider text-[10.5px] text-stone-400 pt-0.5">{label}</div>
    <div className="text-stone-800">{children}</div>
  </div>
);

const STATUS_STYLE = {
  "JUSTIFIED MODELING": "border-emerald-700/40 bg-emerald-50 text-emerald-800",
  "FADING SUCCESS": "border-sky-700/40 bg-sky-50 text-sky-800",
  "REPEATED MODELING": "border-amber-700/40 bg-amber-50 text-amber-800",
};

function FlagCard({ f, i }) {
  const t = f.teacher_language || {};
  return (
    <div className="border border-stone-200 rounded-md p-4 bg-white space-y-2.5" data-testid={`level5-flag-${i}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12.5px] font-medium text-stone-900">{f.operation_label}</span>
        <span className="text-[11px] text-stone-400">turn {f.n}{f.scale ? ` · ${f.scale}` : ""}</span>
        <span className="ml-auto flex flex-wrap gap-1.5" data-testid={`level5-status-${i}`}>
          {(t.instructional_status || []).map((s) => (
            <span key={s} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLE[s] || "border-stone-300 bg-stone-100 text-stone-600"}`}>{s}</span>
          ))}
        </span>
      </div>
      <Row label="Why" testid={`level5-why-${i}`}>{t.why}</Row>
      <Row label="TWS supplied" testid={`level5-supplied-${i}`}>
        <div>{t.tws_supplied}</div>
        {t.excerpt && <blockquote className="mt-1 border-l-2 border-stone-300 pl-2 text-stone-600 italic">“{t.excerpt}”</blockquote>}
      </Row>
      <Row label="Student then" testid={`level5-student-${i}`}>{t.student_then}</Row>
      <Row label="Student skill shown" testid={`level5-skill-${i}`}>{t.student_skill_shown}</Row>
      {t.repeated_note && <Row label="Pattern">{t.repeated_note}</Row>}
      {t.fading_note && <Row label="Fading">{t.fading_note}</Row>}
      <Row label="Next to watch" testid={`level5-next-${i}`}>{t.next_to_watch}</Row>
      <details className="text-[11.5px] text-stone-500">
        <summary className="cursor-pointer hover:text-stone-800">Details</summary>
        <div className="mt-1 space-y-0.5 font-mono break-all">
          <div>episode {f.episode_id} · move turn {f.turn_id}</div>
          <div>escalation level {f.tws_support?.escalation_level ?? "—"} · classifier confidence {f.tws_support?.classifier?.confidence || "—"}</div>
          <div>materially contributed: {String(f.material_contribution?.materially_contributed)}{f.material_contribution?.basis?.length ? ` (${f.material_contribution.basis.join(", ")})` : ""}</div>
          {f.material_contribution?.structural_ids?.length > 0 && <div>relations {f.material_contribution.structural_ids.join(", ")}</div>}
          {f.material_contribution?.unit_ids?.length > 0 && <div>units {f.material_contribution.unit_ids.join(", ")}</div>}
        </div>
      </details>
    </div>
  );
}

export default function StrongScaffoldingPanel({ sessionId }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!sessionId) return;
    let live = true;
    setData(null); setFailed(false);
    getScaffoldingFlags(sessionId).then((d) => live && setData(d)).catch(() => live && setFailed(true));
    return () => { live = false; };
  }, [sessionId]);

  if (failed || !data) return null;
  if (!data.count) {
    return <p className="mt-6 text-[12px] text-stone-400" data-testid="strong-scaffolding-none">No Level-5 (strong) scaffolding recorded in this session.</p>;
  }
  return (
    <section className="mt-6 max-w-2xl" data-testid="strong-scaffolding-panel">
      <div className="text-[11px] font-mono uppercase tracking-wider text-[#8C3A2A] mb-1">Strong scaffolding used</div>
      <p className="text-[13px] text-stone-600 mb-3">
        TWS provided a model of the target structure during this work ({data.count} {data.count === 1 ? "time" : "times"}).
        A structure that appears in the essay after Level-5 modeling is a different condition of performance from one the student produced independently.
      </p>
      {data.summary?.length > 0 && (
        <div className="mb-3 space-y-1.5" data-testid="strong-scaffolding-summary">
          {data.summary.map((s) => (
            <div key={s.operation_label} className="text-[13px] text-stone-800 bg-stone-50 border border-stone-200 rounded-md px-3 py-2">
              <span className="font-medium">{s.operation_label}:</span> {s.lines.join(" ")}
            </div>
          ))}
        </div>
      )}
      <div className="space-y-3">{data.flags.map((f, i) => <FlagCard key={`${f.episode_id}-${f.turn_id}`} f={f} i={i} />)}</div>
    </section>
  );
}
